/**
 * Admin Tickets Management Page
 * Manage all client support tickets
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LifeBuoy,
  Search,
  Filter,
  Clock,
  CheckCircle,
  MessageCircle,
  AlertCircle,
  XCircle,
  User,
  Tag,
  RefreshCw,
  Send,
  Edit,
  Trash2,
  Plus,
} from 'lucide-react';

interface Ticket {
  id: number;
  subject: string;
  clientId: number;
  clientName: string;
  clientEmail: string;
  departmentId: number;
  departmentName: string;
  status: 'open' | 'answered' | 'customer-reply' | 'on-hold' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignedTo?: number;
  assignedStaffName?: string;
  lastReply: string;
  createdAt: string;
  replyCount: number;
}

const statusConfig = {
  open: {
    label: 'Open',
    icon: Clock,
    color: 'text-orange-400',
    bgColor: 'bg-blue-900/20',
  },
  answered: {
    label: 'Answered',
    icon: MessageCircle,
    color: 'text-green-400',
    bgColor: 'bg-green-900/20',
  },
  'customer-reply': {
    label: 'Customer Reply',
    icon: AlertCircle,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-900/20',
  },
  'on-hold': {
    label: 'On Hold',
    icon: Clock,
    color: 'text-orange-400',
    bgColor: 'bg-orange-900/20',
  },
  closed: {
    label: 'Closed',
    icon: CheckCircle,
    color: 'text-gray-400',
    bgColor: 'bg-gray-900/20',
  },
};

const priorityConfig = {
  low: { label: 'Low', color: 'text-gray-400', bgColor: 'bg-gray-900/20' },
  medium: { label: 'Medium', color: 'text-orange-400', bgColor: 'bg-blue-900/20' },
  high: { label: 'High', color: 'text-orange-400', bgColor: 'bg-orange-900/20' },
  urgent: { label: 'Urgent', color: 'text-red-400', bgColor: 'bg-red-900/20' },
};

const TicketsPage: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('open');
  const [filterPriority, setFilterPriority] = useState<string>('all');

  useEffect(() => {
    loadTickets();
  }, [filterStatus]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }

      const response = await fetch(`/api/v1/admin/tickets?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setTickets(data.data.tickets);
      }
    } catch (error) {
      console.error('Failed to load tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTickets = tickets.filter((ticket) => {
    // Filter by search
    const searchMatch =
      searchQuery === '' ||
      ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.departmentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.id.toString().includes(searchQuery);

    // Filter by priority
    const priorityMatch = filterPriority === 'all' || ticket.priority === filterPriority;

    return searchMatch && priorityMatch;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const handleDeleteTicket = async (ticketId: number) => {
    if (!confirm('Are you sure you want to delete this ticket? This action cannot be undone.'))
      return;

    try {
      await fetch(`/api/v1/admin/tickets/${ticketId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      loadTickets();
    } catch (error) {
      console.error('Failed to delete ticket:', error);
      alert('Failed to delete ticket');
    }
  };

  const activeStatuses = ['open', 'answered', 'customer-reply', 'on-hold'];
  const awaitingReply = tickets.filter((t) => t.status === 'customer-reply').length;
  const unassigned = tickets.filter((t) => !t.assignedTo && activeStatuses.includes(t.status))
    .length;
  const urgent = tickets.filter((t) => t.priority === 'urgent' && t.status !== 'closed').length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <LifeBuoy className="w-8 h-8" />
            Support Tickets
          </h1>
          <p className="text-gray-400 mt-2">Manage client support tickets and inquiries</p>
        </div>
        <button className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 flex items-center gap-2 font-medium shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40">
          <Plus className="w-5 h-5" />
          New Ticket
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Tickets</p>
              <p className="text-2xl font-bold text-white mt-1">{tickets.length}</p>
            </div>
            <LifeBuoy className="w-10 h-10 text-orange-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Awaiting Reply</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1">{awaitingReply}</p>
            </div>
            <MessageCircle className="w-10 h-10 text-yellow-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Unassigned</p>
              <p className="text-2xl font-bold text-orange-400 mt-1">{unassigned}</p>
            </div>
            <AlertCircle className="w-10 h-10 text-orange-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Urgent</p>
              <p className="text-2xl font-bold text-red-400 mt-1">{urgent}</p>
            </div>
            <AlertCircle className="w-10 h-10 text-red-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Closed Today</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {
                  tickets.filter(
                    (t) =>
                      t.status === 'closed' &&
                      new Date(t.lastReply).toDateString() === new Date().toDateString()
                  ).length
                }
              </p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by ticket ID, subject, client, or department..."
            className="w-full pl-10 pr-4 py-3 bg-slate-800/80 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-colors"
          />
        </div>

        <div className="flex gap-2">
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-4 py-3 bg-slate-800/80 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-colors"
          >
            <option value="all">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex flex-wrap gap-2">
        {['open', 'customer-reply', 'answered', 'on-hold', 'closed', 'all'].map((status) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              filterStatus === status
                ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
                : 'bg-slate-800/80 text-slate-400 hover:bg-slate-700/50 hover:text-white border border-slate-700/50'
            }`}
          >
            {status === 'customer-reply'
              ? 'Awaiting Reply'
              : status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Tickets List */}
      {loading ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-orange-400 animate-spin" />
            <span className="ml-3 text-gray-400">Loading tickets...</span>
          </div>
        </div>
      ) : filteredTickets.length === 0 ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50 text-center">
          <LifeBuoy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No Tickets Found</h3>
          <p className="text-gray-400">
            {searchQuery ? 'No tickets match your search criteria.' : 'No tickets in this status.'}
          </p>
        </div>
      ) : (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left p-4 text-gray-300 font-medium">Ticket</th>
                  <th className="text-left p-4 text-gray-300 font-medium">Client</th>
                  <th className="text-left p-4 text-gray-300 font-medium">Subject</th>
                  <th className="text-left p-4 text-gray-300 font-medium">Department</th>
                  <th className="text-left p-4 text-gray-300 font-medium">Priority</th>
                  <th className="text-left p-4 text-gray-300 font-medium">Status</th>
                  <th className="text-left p-4 text-gray-300 font-medium">Assigned</th>
                  <th className="text-left p-4 text-gray-300 font-medium">Last Reply</th>
                  <th className="text-right p-4 text-gray-300 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredTickets.map((ticket) => {
                  const statusInfo = statusConfig[ticket.status];
                  const priorityInfo = priorityConfig[ticket.priority];
                  const StatusIcon = statusInfo.icon;

                  return (
                    <tr key={ticket.id} className="hover:bg-slate-700/30">
                      <td className="p-4">
                        <Link
                          to={`/admin/tickets/${ticket.id}`}
                          className="text-orange-400 hover:text-blue-300 font-medium font-mono"
                        >
                          #{ticket.id}
                        </Link>
                      </td>
                      <td className="p-4">
                        <Link
                          to={`/admin/clients/${ticket.clientId}`}
                          className="text-white hover:text-orange-400"
                        >
                          {ticket.clientName}
                        </Link>
                        <div className="text-xs text-gray-400">{ticket.clientEmail}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-white max-w-xs truncate">{ticket.subject}</div>
                        <div className="text-xs text-gray-400">
                          {ticket.replyCount} {ticket.replyCount === 1 ? 'reply' : 'replies'}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-gray-300">{ticket.departmentName}</span>
                      </td>
                      <td className="p-4">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${priorityInfo.bgColor} ${priorityInfo.color}`}
                        >
                          {priorityInfo.label}
                        </span>
                      </td>
                      <td className="p-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bgColor} ${statusInfo.color}`}
                        >
                          <StatusIcon className="w-4 h-4" />
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="p-4">
                        {ticket.assignedStaffName ? (
                          <div className="flex items-center gap-1 text-white">
                            <User className="w-4 h-4 text-gray-400" />
                            {ticket.assignedStaffName}
                          </div>
                        ) : (
                          <span className="text-gray-500 text-sm">Unassigned</span>
                        )}
                      </td>
                      <td className="p-4 text-gray-300 text-sm">{formatDate(ticket.lastReply)}</td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/admin/tickets/${ticket.id}`}
                            className="p-2 text-orange-400 hover:bg-slate-700/50 rounded transition-colors"
                            title="View/Reply"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => handleDeleteTicket(ticket.id)}
                            className="p-2 text-red-400 hover:bg-slate-700/50 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketsPage;
